<script setup>
// Meta Reklam Kutuphanesi taramasi (rakip reklamlari).
//
// Tarama Meta API'sinden degil Apify uzerinden yapiliyor, yani kullanicinin
// KENDI Apify jetonu gerekiyor. Jeton yokken tarama baslatmak her seferinde
// ayni hatayi doguracagi icin sayfa once jeton durumunu soruyor ve jeton
// gelene kadar tarama yuzeyini hic acmiyor.
//
// Taramalar uzun surer ve arka planda calisir; bu yuzden "baslat" ile
// "sonuclari gor" ayri adimlar, calisma listesi de ayri bir tablo.
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import { aracCalistir, diziyeCevir } from './kopru.js';
import AracTablosu from './AracTablosu.vue';

defineProps({
  hesap: { type: String, default: '' },
});

const { t } = useI18n();
const route = useRoute();

// true = kayitli, false = yok, null = arac net soylemedi (ozet metne bakilir)
const jetonDurumu = ref(null);
const jetonOzet = ref('');
const jetonKontrol = ref(false);
const jetonGirisi = ref('');
const jetonIsleniyor = ref(false);

const calismalar = ref([]);
const yukleniyor = ref(false);
const hata = ref('');

const tarama = ref({ query: '', country: 'TR', limit: '' });
const taramaBasliyor = ref(false);

const panelAcik = ref(false);
const panelBaslik = ref('');
const panelVeri = ref(null);
const panelYukleniyor = ref(false);

// Jeton kayitli degilse tarama alanlari hic gosterilmiyor; kayitli olup
// olmadigi anlasilamadiginda (null) kullaniciyi kilitlemek yerine uyarip
// denemesine izin veriyoruz - hatayi zaten arac dondurur.
const taramaAcik = computed(() => jetonDurumu.value !== false);

const JETON_ANAHTARLARI = [
  'has_token',
  'has_apify_token',
  'token_exists',
  'is_registered',
  'registered',
  'configured',
  'valid',
];

// Arac yanitinin sekli belgelenmedigi icin bilinen boolean alanlar taraniyor;
// hicbiri yoksa "bilinmiyor" deyip ozet metni ekranda gosteriyoruz.
const jetonCoz = veri => {
  if (!veri || typeof veri !== 'object') return null;
  const bulunan = JETON_ANAHTARLARI.find(a => typeof veri[a] === 'boolean');
  if (bulunan) return veri[bulunan];
  if (typeof veri.token === 'string') return veri.token.trim().length > 0;
  return null;
};

const jetonDurumuYukle = async () => {
  jetonKontrol.value = true;
  hata.value = '';
  try {
    const sonuc = await aracCalistir(
      route.params.accountId,
      'ads_library_get_apify_token_status'
    );
    jetonDurumu.value = jetonCoz(sonuc.veri);
    jetonOzet.value = sonuc.ozet || '';
  } catch (e) {
    hata.value = e.response?.data?.error || e.message;
    jetonDurumu.value = null;
  } finally {
    jetonKontrol.value = false;
  }
};

const calismalariYukle = async () => {
  yukleniyor.value = true;
  hata.value = '';
  try {
    const sonuc = await aracCalistir(route.params.accountId, 'ads_library_list_runs');
    calismalar.value = diziyeCevir(sonuc);
  } catch (e) {
    hata.value = e.response?.data?.error || e.message;
    calismalar.value = [];
  } finally {
    yukleniyor.value = false;
  }
};

const jetonKaydet = async () => {
  const jeton = jetonGirisi.value.trim();
  if (!jeton) return;
  if (!window.confirm(t('META_ADS.REKLAM_KUTUPHANESI.JETON_KAYDET_ONAY'))) return;
  jetonIsleniyor.value = true;
  hata.value = '';
  try {
    await aracCalistir(
      route.params.accountId,
      'ads_library_register_apify_token',
      { token: jeton },
      { onaylandi: true }
    );
    // Jeton hafizada tutulmuyor: ekranda kalirsa omuz ustunden okunabilir.
    jetonGirisi.value = '';
    useAlert(t('META_ADS.REKLAM_KUTUPHANESI.JETON_KAYDEDILDI'));
    await jetonDurumuYukle();
    // Durum sorgusu hata verdiyse liste cagrisi o hatayi silmesin.
    if (!hata.value) await calismalariYukle();
  } catch (e) {
    hata.value = e.response?.data?.error || e.message;
  } finally {
    jetonIsleniyor.value = false;
  }
};

const jetonSil = async () => {
  if (!window.confirm(t('META_ADS.REKLAM_KUTUPHANESI.JETON_SIL_ONAY'))) return;
  jetonIsleniyor.value = true;
  hata.value = '';
  try {
    await aracCalistir(
      route.params.accountId,
      'ads_library_delete_apify_token',
      {},
      { onaylandi: true }
    );
    useAlert(t('META_ADS.REKLAM_KUTUPHANESI.JETON_SILINDI'));
    await jetonDurumuYukle();
  } catch (e) {
    hata.value = e.response?.data?.error || e.message;
  } finally {
    jetonIsleniyor.value = false;
  }
};

const taramaBaslat = async () => {
  if (!tarama.value.query.trim()) return;
  if (!window.confirm(t('META_ADS.REKLAM_KUTUPHANESI.TARAMA_ONAY'))) return;
  taramaBasliyor.value = true;
  hata.value = '';
  try {
    const argumanlar = { query: tarama.value.query.trim() };
    if (tarama.value.country.trim()) argumanlar.country = tarama.value.country.trim();
    // Limit sayi olarak gonderiliyor; metin gonderilirse arac semasi reddeder.
    if (String(tarama.value.limit).trim()) {
      argumanlar.limit = Number(tarama.value.limit);
    }
    await aracCalistir(
      route.params.accountId,
      'ads_library_scrape',
      argumanlar,
      { onaylandi: true }
    );
    useAlert(t('META_ADS.REKLAM_KUTUPHANESI.TARAMA_BASLADI'));
    await calismalariYukle();
  } catch (e) {
    hata.value = e.response?.data?.error || e.message;
  } finally {
    taramaBasliyor.value = false;
  }
};

const calismaKimligi = satir => satir?.run_id || satir?.id || satir?.runId || '';

// Panelde ham JSON basiliyordu: kullanici "defaultDatasetId" gibi Apify alan
// adlarini goruyordu. Bilinen alanlari Turkce etikete ceviriyoruz; bilinmeyen
// alan adi oldugu gibi kaliyor - gizlemek veriyi kaybetmek olurdu.
const ALAN_ETIKETLERI = {
  id: 'META_ADS.REKLAM_KUTUPHANESI.ALAN.ID',
  run_id: 'META_ADS.REKLAM_KUTUPHANESI.ALAN.CALISMA_ID',
  runId: 'META_ADS.REKLAM_KUTUPHANESI.ALAN.CALISMA_ID',
  status: 'META_ADS.REKLAM_KUTUPHANESI.ALAN.DURUM',
  statusMessage: 'META_ADS.REKLAM_KUTUPHANESI.ALAN.DURUM_ACIKLAMA',
  status_message: 'META_ADS.REKLAM_KUTUPHANESI.ALAN.DURUM_ACIKLAMA',
  query: 'META_ADS.REKLAM_KUTUPHANESI.ALAN.ARAMA',
  country: 'META_ADS.REKLAM_KUTUPHANESI.ALAN.ULKE',
  limit: 'META_ADS.REKLAM_KUTUPHANESI.ALAN.LIMIT',
  itemCount: 'META_ADS.REKLAM_KUTUPHANESI.ALAN.SONUC_SAYISI',
  item_count: 'META_ADS.REKLAM_KUTUPHANESI.ALAN.SONUC_SAYISI',
  startedAt: 'META_ADS.REKLAM_KUTUPHANESI.ALAN.BASLANGIC',
  started_at: 'META_ADS.REKLAM_KUTUPHANESI.ALAN.BASLANGIC',
  finishedAt: 'META_ADS.REKLAM_KUTUPHANESI.ALAN.BITIS',
  finished_at: 'META_ADS.REKLAM_KUTUPHANESI.ALAN.BITIS',
  createdAt: 'META_ADS.REKLAM_KUTUPHANESI.ALAN.OLUSTURMA',
  created_at: 'META_ADS.REKLAM_KUTUPHANESI.ALAN.OLUSTURMA',
  defaultDatasetId: 'META_ADS.REKLAM_KUTUPHANESI.ALAN.VERI_KUMESI',
  actorId: 'META_ADS.REKLAM_KUTUPHANESI.ALAN.TARAYICI',
  exitCode: 'META_ADS.REKLAM_KUTUPHANESI.ALAN.CIKIS_KODU',
  usageTotalUsd: 'META_ADS.REKLAM_KUTUPHANESI.ALAN.MALIYET',
  page_name: 'META_ADS.REKLAM_KUTUPHANESI.ALAN.SAYFA_ADI',
  page_id: 'META_ADS.REKLAM_KUTUPHANESI.ALAN.SAYFA_ID',
  ad_creative_body: 'META_ADS.REKLAM_KUTUPHANESI.ALAN.REKLAM_METNI',
  ad_creative_link_title: 'META_ADS.REKLAM_KUTUPHANESI.ALAN.REKLAM_BASLIGI',
  ad_creative_link_caption: 'META_ADS.REKLAM_KUTUPHANESI.ALAN.REKLAM_BAGLANTISI',
  ad_snapshot_url: 'META_ADS.REKLAM_KUTUPHANESI.ALAN.REKLAM_ONIZLEME',
  ad_delivery_start_time: 'META_ADS.REKLAM_KUTUPHANESI.ALAN.YAYIN_BASLANGICI',
  ad_delivery_stop_time: 'META_ADS.REKLAM_KUTUPHANESI.ALAN.YAYIN_BITISI',
  publisher_platforms: 'META_ADS.REKLAM_KUTUPHANESI.ALAN.YAYIN_YERLERI',
  spend: 'META_ADS.REKLAM_KUTUPHANESI.ALAN.HARCAMA',
  impressions: 'META_ADS.REKLAM_KUTUPHANESI.ALAN.GOSTERIM',
  currency: 'META_ADS.REKLAM_KUTUPHANESI.ALAN.PARA_BIRIMI',
};

const TARIH_ALANLARI = [
  'startedAt',
  'started_at',
  'finishedAt',
  'finished_at',
  'createdAt',
  'created_at',
  'ad_delivery_start_time',
  'ad_delivery_stop_time',
];

// Apify kullanim tutari DOLAR olarak gelir. Ortak paraBicimle kurus bekledigi
// icin burada kullanilamaz - kullansaydik tutar yuze bolunup yanlis gorunurdu.
const PARA_ALANLARI = ['usageTotalUsd'];

const dolarBicimle = deger => {
  const sayi = Number(deger);
  if (Number.isNaN(sayi)) return String(deger);
  return new Intl.NumberFormat('tr-TR', {
    style: 'currency',
    currency: 'USD',
  }).format(sayi);
};

const tarihBicimle = deger => {
  const tarih = new Date(deger);
  return Number.isNaN(tarih.getTime())
    ? String(deger)
    : tarih.toLocaleString('tr-TR');
};

const etiketle = anahtar =>
  ALAN_ETIKETLERI[anahtar] ? t(ALAN_ETIKETLERI[anahtar]) : anahtar;

// Ic ice nesne/dizi tek satira sigmaz; sayisini soyleyip paneli okunur
// tutuyoruz. Bos alan hic basilmiyor: dolu sanilmasin.
const degerBicimle = (anahtar, deger) => {
  if (deger === null || deger === undefined || deger === '') return '';
  if (typeof deger === 'boolean') {
    return deger
      ? t('META_ADS.REKLAM_KUTUPHANESI.PANEL.EVET')
      : t('META_ADS.REKLAM_KUTUPHANESI.PANEL.HAYIR');
  }
  if (Array.isArray(deger)) {
    // Kisa metin dizileri (yayin yerleri gibi) duz listelenince daha anlasilir.
    const duz = deger.every(o => typeof o === 'string' || typeof o === 'number');
    if (duz && deger.length && deger.length <= 6) return deger.join(', ');
    return deger.length
      ? t('META_ADS.REKLAM_KUTUPHANESI.PANEL.OGE_SAYISI', { sayi: deger.length })
      : '';
  }
  if (typeof deger === 'object') {
    const sayi = Object.keys(deger).length;
    return sayi
      ? t('META_ADS.REKLAM_KUTUPHANESI.PANEL.ALAN_SAYISI', { sayi })
      : '';
  }
  if (PARA_ALANLARI.includes(anahtar)) return dolarBicimle(deger);
  if (TARIH_ALANLARI.includes(anahtar)) return tarihBicimle(deger);
  return String(deger);
};

// Sonuc listesi yuzlerce reklam donebilir; tamamini basmak paneli kilitler.
const EN_FAZLA_KAYIT = 20;

const panelTumKayitlar = computed(() => {
  const v = panelVeri.value;
  if (!v || typeof v !== 'object') return [];
  return (Array.isArray(v) ? v : [v])
    .filter(kayit => kayit && typeof kayit === 'object')
    .map(kayit =>
      Object.entries(kayit)
        .map(([anahtar, deger]) => ({
          anahtar,
          etiket: etiketle(anahtar),
          deger: degerBicimle(anahtar, deger),
        }))
        .filter(alan => alan.deger !== '')
    )
    .filter(kayit => kayit.length);
});

const panelKayitlari = computed(() =>
  panelTumKayitlar.value.slice(0, EN_FAZLA_KAYIT)
);

// Kirpma sessiz kalmasin: kullanici kac sonucun gorunmedigini bilmeli.
const panelKirpildi = computed(
  () => panelTumKayitlar.value.length > EN_FAZLA_KAYIT
);

// Arac yalnizca metin ozeti urettiyse onu oldugu gibi gosteriyoruz.
const panelMetni = computed(() =>
  typeof panelVeri.value === 'string' ? panelVeri.value : ''
);

const panelBos = computed(
  () => !panelMetni.value && !panelTumKayitlar.value.length
);

const paneliAc = async (satir, arac, baslik) => {
  const kimlik = calismaKimligi(satir);
  if (!kimlik) return;
  panelAcik.value = true;
  panelBaslik.value = baslik;
  panelVeri.value = null;
  panelYukleniyor.value = true;
  hata.value = '';
  try {
    const sonuc = await aracCalistir(route.params.accountId, arac, { run_id: kimlik });
    panelVeri.value = sonuc.veri ?? sonuc.ozet ?? null;
  } catch (e) {
    hata.value = e.response?.data?.error || e.message;
    panelAcik.value = false;
  } finally {
    panelYukleniyor.value = false;
  }
};

const durumGoster = satir =>
  paneliAc(satir, 'ads_library_get_run_status', t('META_ADS.REKLAM_KUTUPHANESI.DURUM_BASLIK'));

const sonucGoster = satir =>
  paneliAc(satir, 'ads_library_get_results', t('META_ADS.REKLAM_KUTUPHANESI.SONUC_BASLIK'));

const calismayiDurdur = async satir => {
  const kimlik = calismaKimligi(satir);
  if (!kimlik) return;
  // Durdurma geri alinamaz ve Apify kredisi zaten harcanmis olur.
  if (!window.confirm(t('META_ADS.REKLAM_KUTUPHANESI.DURDUR_ONAY'))) return;
  hata.value = '';
  try {
    await aracCalistir(
      route.params.accountId,
      'ads_library_abort_run',
      { run_id: kimlik },
      { onaylandi: true }
    );
    useAlert(t('META_ADS.REKLAM_KUTUPHANESI.DURDURULDU'));
    await calismalariYukle();
  } catch (e) {
    hata.value = e.response?.data?.error || e.message;
  }
};

onMounted(async () => {
  await jetonDurumuYukle();
  // Jeton sorgusu hata verdiyse calisma listesi ayni sebepten patlar; ikinci
  // cagri basta hata alanini temizledigi icin kullanici sebebi hic goremezdi.
  if (!hata.value && jetonDurumu.value !== false) await calismalariYukle();
});
</script>

<template>
  <div class="p-6">
    <div v-if="jetonKontrol" class="flex justify-center p-10">
      <Spinner class="text-n-brand" />
    </div>

    <template v-else>
      <p v-if="hata" class="mb-3 text-sm text-n-ruby-11">{{ hata }}</p>

      <!-- Jeton paneli: yoksa tek yuzey budur, varsa yalnizca durum satiri -->
      <div class="p-4 mb-5 border rounded-lg border-n-weak">
        <div class="flex flex-wrap items-center gap-2">
          <h2 class="text-sm font-medium text-n-slate-12">
            {{ t('META_ADS.REKLAM_KUTUPHANESI.JETON_BASLIK') }}
          </h2>
          <span
            class="text-xs"
            :class="jetonDurumu === true ? 'text-n-teal-11' : 'text-n-ruby-11'"
          >
            {{
              jetonDurumu === true
                ? t('META_ADS.REKLAM_KUTUPHANESI.JETON_VAR')
                : jetonDurumu === false
                  ? t('META_ADS.REKLAM_KUTUPHANESI.JETON_YOK')
                  : t('META_ADS.REKLAM_KUTUPHANESI.JETON_BILINMIYOR')
            }}
          </span>
          <span class="grow" />
          <Button
            v-if="jetonDurumu === true"
            size="sm"
            variant="faded"
            icon="i-lucide-trash-2"
            :is-loading="jetonIsleniyor"
            :label="t('META_ADS.REKLAM_KUTUPHANESI.JETON_SIL')"
            @click="jetonSil"
          />
        </div>

        <p v-if="jetonOzet" class="mt-2 text-xs text-n-slate-11">{{ jetonOzet }}</p>

        <form
          v-if="jetonDurumu !== true"
          class="flex flex-wrap items-end gap-2 mt-3"
          @submit.prevent="jetonKaydet"
        >
          <label class="text-sm text-n-slate-11 grow">
            {{ t('META_ADS.REKLAM_KUTUPHANESI.JETON_ETIKET') }}
            <!-- required: bos gonderimde kod sessizce donuyordu, dugme bozuk sanilirdi -->
            <input
              v-model="jetonGirisi"
              type="password"
              autocomplete="off"
              required
              class="!mb-0"
              placeholder="apify_api_..."
            />
          </label>
          <Button
            size="sm"
            variant="solid"
            type="submit"
            :is-loading="jetonIsleniyor"
            :label="t('META_ADS.ORTAK.KAYDET')"
          />
        </form>

        <p v-if="jetonDurumu !== true" class="mt-2 text-xs text-n-slate-11">
          {{ t('META_ADS.REKLAM_KUTUPHANESI.JETON_IPUCU') }}
        </p>
      </div>

      <template v-if="taramaAcik">
        <form
          class="grid gap-3 p-4 mb-5 border rounded-lg sm:grid-cols-4 border-n-weak"
          @submit.prevent="taramaBaslat"
        >
          <label class="text-sm sm:col-span-2 text-n-slate-11">
            {{ t('META_ADS.REKLAM_KUTUPHANESI.ARAMA') }}
            <!-- required: arama bos kalirsa taramaBaslat sessizce donuyordu -->
            <input
              v-model="tarama.query"
              type="text"
              required
              :placeholder="t('META_ADS.REKLAM_KUTUPHANESI.ARAMA_IPUCU')"
            />
          </label>
          <label class="text-sm text-n-slate-11">
            {{ t('META_ADS.REKLAM_KUTUPHANESI.ULKE') }}
            <input v-model="tarama.country" type="text" placeholder="TR" />
          </label>
          <label class="text-sm text-n-slate-11">
            {{ t('META_ADS.REKLAM_KUTUPHANESI.LIMIT') }}
            <input v-model="tarama.limit" type="number" min="1" />
          </label>
          <div class="flex gap-2 sm:col-span-4">
            <Button
              size="sm"
              variant="solid"
              type="submit"
              icon="i-lucide-search"
              :is-loading="taramaBasliyor"
              :label="t('META_ADS.REKLAM_KUTUPHANESI.TARA')"
            />
            <Button
              type="button"
              size="sm"
              variant="faded"
              icon="i-lucide-refresh-cw"
              :label="t('META_ADS.ORTAK.YENILE')"
              @click="calismalariYukle"
            />
          </div>
        </form>

        <h3 class="mb-2 text-sm font-medium text-n-slate-12">
          {{ t('META_ADS.REKLAM_KUTUPHANESI.CALISMALAR') }}
        </h3>

        <!--
          Sutunlar bilerek verilmedi: calisma kaydinin alanlari MCP surumune
          gore degisiyor, sabitlemek yeni alanlari gizlerdi. AracTablosu ilk
          kayittan turetiyor.
        -->
        <AracTablosu
          :satirlar="calismalar"
          :yukleniyor="yukleniyor"
          :bos-metin="t('META_ADS.REKLAM_KUTUPHANESI.BOS')"
        >
          <template #eylemler="{ satir }">
            <Button
              size="sm"
              variant="faded"
              :label="t('META_ADS.REKLAM_KUTUPHANESI.DURUM')"
              @click="durumGoster(satir)"
            />
            <Button
              size="sm"
              variant="faded"
              class="ml-2"
              :label="t('META_ADS.REKLAM_KUTUPHANESI.SONUCLAR')"
              @click="sonucGoster(satir)"
            />
            <Button
              size="sm"
              variant="faded"
              class="ml-2"
              :label="t('META_ADS.REKLAM_KUTUPHANESI.DURDUR')"
              @click="calismayiDurdur(satir)"
            />
          </template>
        </AracTablosu>
      </template>

      <div
        v-if="panelAcik"
        class="fixed inset-0 z-50 flex items-center justify-center p-6 bg-n-alpha-black2"
        @click.self="panelAcik = false"
      >
        <div
          class="w-full max-w-3xl max-h-[80vh] overflow-auto rounded-lg bg-n-surface-1 border border-n-weak"
        >
          <div class="flex items-center gap-2 px-4 py-3 border-b border-n-weak">
            <h3 class="text-sm font-medium text-n-slate-12">{{ panelBaslik }}</h3>
            <span class="grow" />
            <Button
              size="sm"
              variant="faded"
              :label="t('META_ADS.ORTAK.KAPAT')"
              @click="panelAcik = false"
            />
          </div>
          <div class="p-4">
            <div v-if="panelYukleniyor" class="flex justify-center p-8">
              <Spinner class="text-n-brand" />
            </div>
            <template v-else>
              <p
                v-if="panelMetni"
                class="text-sm whitespace-pre-line text-n-slate-11"
              >
                {{ panelMetni }}
              </p>

              <div
                v-for="(kayit, i) in panelKayitlari"
                :key="i"
                class="py-3 border-b last:border-b-0 border-n-weak"
              >
                <dl class="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
                  <template v-for="alan in kayit" :key="alan.anahtar">
                    <dt class="text-n-slate-11">{{ alan.etiket }}</dt>
                    <dd class="break-words text-n-slate-12">{{ alan.deger }}</dd>
                  </template>
                </dl>
              </div>

              <p v-if="panelKirpildi" class="pt-3 text-xs text-n-slate-11">
                {{
                  t('META_ADS.REKLAM_KUTUPHANESI.PANEL.KISALTILDI', {
                    gosterilen: panelKayitlari.length,
                    toplam: panelTumKayitlar.length,
                  })
                }}
              </p>

              <p v-if="panelBos" class="py-6 text-sm text-center text-n-slate-11">
                {{ t('META_ADS.REKLAM_KUTUPHANESI.PANEL.BOS') }}
              </p>
            </template>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>
